import type { ReactNode } from 'react';
import { GeometricPattern } from '@/layouts/PublicShell';

/**
 * Masthead for the inner marketing pages.
 *
 * Shorter than the home hero on purpose: these pages exist to be read, and a
 * full-height banner above a policy document just pushes the text below the
 * fold.
 */
export function PageHero({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <section className="relative overflow-hidden border-b border-border bg-background-alt py-14 md:py-20">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080801a_1px,transparent_1px),linear-gradient(to_bottom,#8080801a_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_40%,#000_50%,transparent_100%)]" />
      <GeometricPattern className="pointer-events-none absolute -right-20 -top-16 hidden h-80 w-80 text-primary/[0.05] md:block" />

      <div className="container relative">
        <div className="mx-auto max-w-3xl text-center">
          <p className="eyebrow mb-3">{eyebrow}</p>
          <h1 className="font-heading text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl md:text-5xl">
            {title}
          </h1>
          {description && (
            <p className="mt-4 text-base leading-relaxed text-foreground-secondary sm:text-lg">
              {description}
            </p>
          )}
          {children}
        </div>
      </div>
    </section>
  );
}

/** A heading and body block inside a policy document. */
export function Clause({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="scroll-mt-24" id={`clause-${number}`}>
      <h2 className="mb-3 flex gap-3 font-heading text-lg font-bold text-foreground sm:text-xl">
        <span className="numeric shrink-0 text-primary">{number}</span>
        {title}
      </h2>
      <div className="space-y-3 pl-0 text-[15px] leading-relaxed text-foreground-secondary sm:pl-9">
        {children}
      </div>
    </section>
  );
}

/** Wrapper that gives long-form text a comfortable measure on every screen. */
export function Prose({ children }: { children: ReactNode }) {
  return (
    <div className="container py-14 md:py-20">
      <div className="mx-auto max-w-3xl space-y-10">{children}</div>
    </div>
  );
}
